import { useValue } from "@tldraw/state-react";
import {
  defaultShapeSchemas,
  toRichText,
  type TLGeoShape,
  type TLNoteShape,
} from "@tldraw/tlschema";
import type { Extensions } from "@tiptap/core";
import { useEditor } from "../../hooks";
import { Vec, type VecLike } from "../../geometry";
import { Ellipse2d, Polygon2d, Rectangle2d, type Geometry2d } from "../geometry2d";
import { BaseBoxShapeUtil, HTMLContainer, SVGContainer, resizeBox, type ResizeInfo } from "../shape-util";
import { defaultExtensions, isEmptyRichText, RichTextLabel } from "./rich-label";
import {
  LABEL_FONT_SIZE,
  LABEL_PADDING,
  NOTE_SIZE,
  colorOf,
  dashArray,
  fillPaint,
  fontFamily,
  STROKE_WIDTH,
  type DashStyle,
  type FillStyle,
  type SizeStyle,
} from "./styles";

// Geometric shapes (rectangles, ellipses, revision clouds…) and sticky notes,
// each with an optional rich-text label.

type Outline = { points: Vec[]; path: string; closed: true };

const regular = (n: number, w: number, h: number, start = -Math.PI / 2) => {
  const pts = Array.from({ length: n }, (_, i) => {
    const a = start + (i / n) * Math.PI * 2;
    return new Vec(Math.cos(a), Math.sin(a));
  });
  // Stretch the unit polygon to fill the box exactly.
  const xs = pts.map((p) => p.x),
    ys = pts.map((p) => p.y);
  const [minX, maxX, minY, maxY] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  return pts.map((p) => new Vec(((p.x - minX) / (maxX - minX)) * w, ((p.y - minY) / (maxY - minY)) * h));
};

const polygonPath = (points: VecLike[]) => `M${points.map((p) => `${p.x},${p.y}`).join("L")}Z`;

/** Points of an ellipse inscribed in the box. */
const ellipsePoints = (w: number, h: number, n = 64) =>
  Array.from({ length: n }, (_, i) => {
    const a = (i / n) * Math.PI * 2;
    return new Vec(w / 2 + (Math.cos(a) * w) / 2, h / 2 + (Math.sin(a) * h) / 2);
  });

/** A revision cloud: semicircular bumps bulging outwards along the box edges. */
function cloud(w: number, h: number, size: number): Outline {
  const bump = Math.max(10, Math.min(60, Math.min(w, h) / 4)) * (size / 3.5) ** 0.3;
  // Bumps are at most `bump` wide, so insetting by half keeps them inside the box.
  const i0 = Math.min(bump / 2, w / 4, h / 4);
  const corners = [new Vec(i0, i0), new Vec(w - i0, i0), new Vec(w - i0, h - i0), new Vec(i0, h - i0)];
  const points: Vec[] = [];
  for (let i = 0; i < 4; i++) {
    const a = corners[i],
      b = corners[(i + 1) % 4];
    const n = Math.max(1, Math.ceil(Vec.Dist(a, b) / bump));
    for (let j = 0; j < n; j++) points.push(Vec.Lrp(a, b, j / n));
  }
  let path = `M${points[0].x},${points[0].y}`;
  for (let i = 0; i < points.length; i++) {
    const a = points[i],
      b = points[(i + 1) % points.length];
    const r = Vec.Dist(a, b) / 2;
    path += `A${r},${r} 0 0 1 ${b.x},${b.y}`;
  }
  return { points, path: `${path}Z`, closed: true };
}

export function geoOutline(geo: string, w: number, h: number, strokeSize = 3.5): Outline {
  const poly = (points: Vec[]): Outline => ({ points, path: polygonPath(points), closed: true });
  const o = Math.min(w, h) / 4;
  switch (geo) {
    case "ellipse":
      return poly(ellipsePoints(w, h));
    case "oval": {
      const r = Math.min(w, h) / 2;
      const pts: Vec[] = [];
      const arc = (cx: number, cy: number, from: number) => {
        for (let i = 0; i <= 16; i++) {
          const a = from + (i / 16) * Math.PI;
          pts.push(new Vec(cx + Math.cos(a) * r, cy + Math.sin(a) * r));
        }
      };
      if (w >= h) {
        arc(w - r, r, -Math.PI / 2);
        arc(r, r, Math.PI / 2);
      } else {
        arc(r, h - r, 0);
        arc(r, r, Math.PI);
      }
      return poly(pts);
    }
    case "triangle":
      return poly([new Vec(w / 2, 0), new Vec(w, h), new Vec(0, h)]);
    case "diamond":
      return poly([new Vec(w / 2, 0), new Vec(w, h / 2), new Vec(w / 2, h), new Vec(0, h / 2)]);
    case "pentagon":
      return poly(regular(5, w, h));
    case "hexagon":
      return poly(regular(6, w, h, 0));
    case "octagon":
      return poly(regular(8, w, h, Math.PI / 8));
    case "star": {
      const pts: Vec[] = [];
      for (let i = 0; i < 10; i++) {
        const a = -Math.PI / 2 + (i / 10) * Math.PI * 2;
        const r = i % 2 ? 0.42 : 1;
        pts.push(new Vec(Math.cos(a) * r, Math.sin(a) * r));
      }
      const xs = pts.map((p) => p.x),
        ys = pts.map((p) => p.y);
      const [minX, maxX, minY, maxY] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
      return poly(pts.map((p) => new Vec(((p.x - minX) / (maxX - minX)) * w, ((p.y - minY) / (maxY - minY)) * h)));
    }
    case "rhombus":
      return poly([new Vec(o, 0), new Vec(w, 0), new Vec(w - o, h), new Vec(0, h)]);
    case "rhombus-2":
      return poly([new Vec(0, 0), new Vec(w - o, 0), new Vec(w, h), new Vec(o, h)]);
    case "trapezoid":
      return poly([new Vec(o, 0), new Vec(w - o, 0), new Vec(w, h), new Vec(0, h)]);
    case "arrow-right":
    case "arrow-left":
    case "arrow-up":
    case "arrow-down": {
      // A block arrow pointing right, rotated into place.
      const horizontal = geo === "arrow-right" || geo === "arrow-left";
      const [L, T] = horizontal ? [w, h] : [h, w];
      const head = Math.min(L / 2, T);
      const unit = [
        [0, T * 0.25], [L - head, T * 0.25], [L - head, 0], [L, T / 2],
        [L - head, T], [L - head, T * 0.75], [0, T * 0.75],
      ];
      const map = ([x, y]: number[]) =>
        geo === "arrow-right" ? new Vec(x, y)
        : geo === "arrow-left" ? new Vec(L - x, y)
        : geo === "arrow-down" ? new Vec(y, x)
        : new Vec(y, L - x);
      return poly(unit.map(map));
    }
    case "heart": {
      const pts: Vec[] = [];
      for (let i = 0; i < 64; i++) {
        const t = (i / 64) * Math.PI * 2;
        const x = 16 * Math.sin(t) ** 3,
          y = -(13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t));
        pts.push(new Vec(x, y));
      }
      const xs = pts.map((p) => p.x),
        ys = pts.map((p) => p.y);
      const [minX, maxX, minY, maxY] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
      return poly(pts.map((p) => new Vec(((p.x - minX) / (maxX - minX)) * w, ((p.y - minY) / (maxY - minY)) * h)));
    }
    case "cloud":
      return cloud(w, h, strokeSize);
    default:
      // rectangle, x-box and check-box share the rectangle outline.
      return poly([new Vec(0, 0), new Vec(w, 0), new Vec(w, h), new Vec(0, h)]);
  }
}

export class GeoShapeUtil extends BaseBoxShapeUtil<TLGeoShape> {
  static override type = "geo";
  static override props = defaultShapeSchemas.geo.props;
  extensions: Extensions = defaultExtensions;
  getDefaultProps(): TLGeoShape["props"] {
    return {
      w: 100,
      h: 100,
      geo: "rectangle",
      dash: "draw",
      growY: 0,
      url: "",
      scale: 1,
      flipX: false,
      flipY: false,
      labelColor: "black",
      color: "black",
      fill: "none",
      size: "m",
      font: "draw",
      align: "middle",
      verticalAlign: "middle",
      richText: toRichText(""),
    } as TLGeoShape["props"];
  }
  private size(shape: TLGeoShape) {
    return { w: shape.props.w, h: shape.props.h + shape.props.growY };
  }
  override getGeometry(shape: TLGeoShape): Geometry2d {
    const { w, h } = this.size(shape);
    const filled = shape.props.fill !== "none";
    if (shape.props.geo === "rectangle" || shape.props.geo === "x-box" || shape.props.geo === "check-box")
      return new Rectangle2d({ width: w, height: h, isFilled: filled });
    if (shape.props.geo === "ellipse") return new Ellipse2d({ width: w, height: h, isFilled: filled });
    return new Polygon2d({ points: geoOutline(shape.props.geo, w, h).points, isFilled: filled });
  }
  override getIndicatorPath(shape: TLGeoShape) {
    const { w, h } = this.size(shape);
    return geoOutline(shape.props.geo, w, h, STROKE_WIDTH[shape.props.size as SizeStyle]).path;
  }
  override canEdit() {
    return true;
  }
  override getText(shape: TLGeoShape) {
    return isEmptyRichText(shape.props.richText) ? "" : "label";
  }
  override onResize(shape: TLGeoShape, info: ResizeInfo<TLGeoShape>) {
    const resized = resizeBox(shape, info) as { props: { w: number; h: number } };
    // Resizing absorbs any extra height a long label added.
    return { ...resized, props: { ...resized.props, growY: 0 } } as never;
  }
  component(shape: TLGeoShape) {
    return <GeoBody shape={shape} util={this} />;
  }
}

function GeoBody({ shape, util }: { shape: TLGeoShape; util: GeoShapeUtil }) {
  const editor = useEditor();
  const editing = useValue("editing geo", () => editor.getEditingShapeId() === shape.id, [editor, shape.id]);
  const { w } = shape.props;
  const h = shape.props.h + shape.props.growY;
  const stroke = STROKE_WIDTH[shape.props.size as SizeStyle] * shape.props.scale;
  const outline = geoOutline(shape.props.geo, w, h, stroke);
  const color = colorOf(editor, String(shape.meta.exactStrokeColor??shape.meta.exactColor??shape.props.color));
  const fill = fillPaint(editor, String(shape.meta.exactFillColor??shape.meta.exactColor??shape.props.color), shape.props.fill as FillStyle);
  const hatch = `hatch-${shape.id.replaceAll(":", "-")}`;
  const patterned = shape.props.fill === "pattern" || shape.props.fill === "lined-fill";
  const hasLabel = editing || !isEmptyRichText(shape.props.richText);
  return (
    <>
      <SVGContainer>
        {patterned && (
          <defs>
            <pattern id={hatch} width={8} height={8} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <rect width={8} height={8} fill={color.semi} />
              <line x1={0} y1={0} x2={0} y2={8} stroke={color.pattern} strokeWidth={2} />
            </pattern>
          </defs>
        )}
        <path
          d={outline.path}
          fill={patterned ? `url(#${hatch})` : fill}
          stroke={color.solid}
          strokeWidth={stroke}
          strokeLinejoin="round"
          strokeDasharray={dashArray(shape.props.dash as DashStyle, stroke)}
        />
        {shape.props.geo === "x-box" && (
          <path d={`M0,0L${w},${h}M${w},0L0,${h}`} stroke={color.solid} strokeWidth={stroke} />
        )}
        {shape.props.geo === "check-box" && (
          <path
            d={`M${w * 0.25},${h * 0.52}L${w * 0.45},${h * 0.72}L${w * 0.78},${h * 0.3}`}
            fill="none"
            stroke={color.solid}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </SVGContainer>
      {hasLabel && (
        <HTMLContainer
          style={{
            width: w,
            height: h,
            display: "flex",
            alignItems: { start: "flex-start", middle: "center", end: "flex-end" }[shape.props.verticalAlign] ?? "center",
            justifyContent: "center",
            pointerEvents: editing ? "auto" : "none",
          }}
        >
          <RichTextLabel
            editor={editor}
            shapeId={shape.id}
            type="geo"
            richText={shape.props.richText}
            editing={editing}
            extensions={util.extensions}
            style={{
              maxWidth: w - LABEL_PADDING * 2,
              padding: LABEL_PADDING / 2,
              fontFamily: fontFamily(editor, shape.props.font),
              fontSize: LABEL_FONT_SIZE[shape.props.size as SizeStyle] * shape.props.scale,
              lineHeight: 1.35,
              color: colorOf(editor, shape.props.labelColor).solid,
              textAlign: ({ start: "left", middle: "center", end: "right" } as Record<string, "left" | "center" | "right">)[shape.props.align] ?? "center",
              overflowWrap: "break-word",
            }}
          />
        </HTMLContainer>
      )}
    </>
  );
}

export class NoteShapeUtil extends BaseBoxShapeUtil<TLNoteShape & { props: { w: number; h: number } }> {
  static override type = "note";
  static override props = defaultShapeSchemas.note.props;
  extensions: Extensions = defaultExtensions;
  getDefaultProps() {
    return {
      color: "yellow",
      labelColor: "black",
      size: "m",
      font: "draw",
      fontSizeAdjustment: 0,
      align: "middle",
      verticalAlign: "middle",
      growY: 0,
      url: "",
      richText: toRichText(""),
      scale: 1,
      textLastEditedBy: null,
    } as never;
  }
  private dims(shape: TLNoteShape) {
    const s = NOTE_SIZE * shape.props.scale;
    return { w: s, h: s + shape.props.growY * shape.props.scale };
  }
  override getGeometry(shape: TLNoteShape) {
    const { w, h } = this.dims(shape);
    return new Rectangle2d({ width: w, height: h, isFilled: true });
  }
  override getIndicatorPath(shape: TLNoteShape) {
    const { w, h } = this.dims(shape);
    return `M0,0H${w}V${h}H0Z`;
  }
  override canResize() {
    return false;
  }
  override hideResizeHandles() {
    return true;
  }
  override canEdit() {
    return true;
  }
  override getText(shape: TLNoteShape) {
    return isEmptyRichText(shape.props.richText) ? "" : "note";
  }
  override component(shape: TLNoteShape) {
    return <NoteBody shape={shape} util={this} />;
  }
}

function NoteBody({ shape, util }: { shape: TLNoteShape; util: NoteShapeUtil }) {
  const editor = useEditor();
  const editing = useValue("editing note", () => editor.getEditingShapeId() === shape.id, [editor, shape.id]);
  const s = NOTE_SIZE * shape.props.scale;
  const h = s + shape.props.growY * shape.props.scale;
  const color = colorOf(editor, String(shape.meta.exactStrokeColor??shape.meta.exactColor??shape.props.color));
  return (
    <HTMLContainer
      className="canvas-note"
      style={{
        width: s,
        height: h,
        background: color.noteFill,
        boxShadow: "0 2px 6px rgba(0,0,0,.12), 0 8px 20px rgba(0,0,0,.06)",
        display: "flex",
        alignItems: { start: "flex-start", middle: "center", end: "flex-end" }[shape.props.verticalAlign] ?? "center",
        justifyContent: "center",
        pointerEvents: editing ? "auto" : "none",
      }}
    >
      <RichTextLabel
        editor={editor}
        shapeId={shape.id}
        type="note"
        richText={shape.props.richText}
        editing={editing}
        extensions={util.extensions}
        style={{
          width: "100%",
          padding: LABEL_PADDING,
          fontFamily: fontFamily(editor, shape.props.font),
          // fontSizeAdjustment shrinks long text as a factor; 0 means not set.
          fontSize: LABEL_FONT_SIZE[shape.props.size as SizeStyle] * (shape.props.fontSizeAdjustment || 1) * shape.props.scale,
          lineHeight: 1.35,
          color: color.noteText,
          textAlign: ({ start: "left", middle: "center", end: "right" } as Record<string, "left" | "center" | "right">)[shape.props.align] ?? "center",
          overflowWrap: "break-word",
        }}
      />
    </HTMLContainer>
  );
}
